import { isEqual } from "lodash";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRequest } from "redux-query-react";
import { useGetFundsAndCffModels } from "./use-get-funds-and-models";
import { FundModelResponseFromJSON, postCffFundsModels, usePostCffDefaultModels } from "api/admin/src";
import { useGetFunds } from "api/ux/src";
import { restoreSelectedFundsToDefault } from "store/administration/cashflow-forecasting/apply-models";
export const modelsAssociationsQueryKey = "AllModelsAssociations";
export const useGetFundsCashflowModels = () => {
    const [funds, , forceFundRefresh] = useGetFunds();
    const flatFunds = useMemo(() => funds?.map((f) => ({
        assetClass: f.assetClass,
        fundId: f.id || "",
        requestId: f.id || "",
        strategy: f.strategy
    })).concat(funds?.flatMap((f, i) => f.subscriptions?.map((s) => ({
        assetClass: f.assetClass,
        fundId: f.id || "",
        investorAccountId: s.investmentId,
        requestId: `${f.id}-${i}`,
        strategy: f.strategy
    })) ?? [])), [funds]);
    const modelsRequest = postCffFundsModels({ fundModelRequest: flatFunds }, {
        queryKey: modelsAssociationsQueryKey,
        transform: (body) => {
            const objResponse = body.map(FundModelResponseFromJSON);
            return {
                FundModelAssociations: objResponse.reduce((map, obj) => {
                    map[obj.investorAccountId ? `${obj.fundId}-${obj.investorAccountId}` : obj.fundId ?? ""] = obj;
                    return map;
                }, {})
            };
        },
        update: {
            FundModelAssociations: (prevValue, transformedValue) => ({ ...prevValue, ...transformedValue })
        }
    });
    const [, refresh] = useRequest(funds?.length ? modelsRequest : null);
    const globalQueryState = useSelector((state) => state?.customQueries?.[modelsAssociationsQueryKey], isEqual);
    const combinedRefresh = () => {
        refresh();
        forceFundRefresh();
    };
    const fundsModels = useSelector((state) => state.entities.FundModelAssociations, isEqual);
    return [fundsModels, globalQueryState, combinedRefresh];
};
export const useGetFundsDefaultModels = () => {
    const dispatch = useDispatch();
    const [, mutation] = usePostCffDefaultModels({
        transform: (body) => {
            const objResponse = body.map(FundModelResponseFromJSON);
            return {
                FundDefaultModel: objResponse.reduce((map, obj) => {
                    map[obj.investorAccountId ? `${obj.fundId}-${obj.investorAccountId}` : obj.fundId ?? ""] = obj;
                    return map;
                }, {})
            };
        },
        update: {
            FundDefaultModel: (prevValue, transformedValue) => ({ ...prevValue, ...transformedValue })
        }
    });
    const { fundsAndCffModels } = useGetFundsAndCffModels();
    const funds = Object.values(fundsAndCffModels);
    const mut = (selectedFunds) => {
        const flatFunds = selectedFunds.map((f, i) => {
            const fund = funds.find((x) => x.fundId === f.fundId);
            if (f.investorAccId === "") {
                return {
                    assetClass: fund?.assetClass,
                    fundId: fund?.fundId || "",
                    requestId: fund?.fundId || "",
                    strategy: fund?.strategy
                };
            }
            return {
                assetClass: fund?.assetClass,
                fundId: fund?.fundId || "",
                investorAccountId: f.investorAccId,
                requestId: `${f.fundId}-${i}`,
                strategy: fund?.strategy
            };
        });
        mutation(flatFunds, {})?.then((value) => {
            const response = value.body;
            dispatch(restoreSelectedFundsToDefault(response.map((r) => ({ defaultModelId: r.cashflowModelId ?? "N/A",
                fundId: r.fundId,
                investorAccId: r.investorAccountId }))));
        });
    };
    return mut;
};
